import * as React from "react";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  styled,
  TextField,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import {
  KahunPrimaryTypography,
  KahunSecondaryTypography,
} from "fe-web-components/styles/typography";
import { Specialities } from "user-management/src/cognito/specialities";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import {
  TERMS_OF_USE_URL,
  PRIVACY_POLICY_URL,
  COOKIE_POLICY_URL,
} from "user-management/src/components/termAndConditionsLinks";
import {
  authenticateUser,
  confirmSignUp,
  signUp,
} from "user-management/src/cognito/kahunUserAuth";

export const useQueryParams = () => new URLSearchParams(window.location.search);

//region [[ Styles ]]
const TermConfirmText = styled("div")({
  fontSize: 14,
  lineHeight: 1.2,
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface SignUpFormProps {
  onSignUpComplete: () => void;
  onError?: (error: string) => void;
  children?: React.ReactNode;
  requireFullName?: boolean;
  requireSpecialty?: boolean;
  requireTermsConfirm?: boolean;
  // An identification to let us know from where the user had subscribed
  // (patient, agent, new england, clinic management?)
  platform?: string;
  customFields?: Record<string, string>;
  customClientData?: Record<string, string>;
}
//endregion [[ Props ]]

export const SignUpForm = ({
  onSignUpComplete,
  onError,
  requireTermsConfirm = true,
  customClientData = {},
  ...props
}: SignUpFormProps) => {
  const defaultSpecialty = ""; //localStorage.getItem("custom:specialty");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [verifyPassword, setVerifyPassword] = useState("");
  const [termsConfirmed, setTermsConfirmed] = useState(false);

  const [verificationCodeFn, setVerificationCodeFn] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [specialty, setSpecialty] = useState(defaultSpecialty || "");
  const queryParam = useQueryParams();
  const partner = queryParam.get("pid");

  const onSignUp = useCallback(
    (e) => {
      evt({
        category: CATEGORY.USER_AUTHENTICATION,
        action: ACTION.SIGN_UP,
        label: email,
        props: {
          platform: props.platform,
          userId: email,
        },
      });
      e.preventDefault();
      (async () => {
        try {
          const verificationCodeFn = await signUp(
            email,
            password,
            specialty,
            partner || props.platform,
            {
              given_name: firstName,
              family_name: lastName,
              ...(props.customFields || {}),
            },
            customClientData,
          );
          onError("");
          if (customClientData.autoVerify !== "true") {
            setVerificationCodeFn(verificationCodeFn);
          }
        } catch (e) {
          onError(e["message"] || e);
          evt({
            category: CATEGORY.USER_AUTHENTICATION,
            action: ACTION.SIGN_UP_FAILED,
            label: `email: ${email}, error: ${e}`,
            props: {
              userId: email,
              platform: props.platform,
            },
          });
        }
      })();
    },
    [setVerificationCodeFn, email, password, specialty],
  );

  const handleVerifyActivationCode = useCallback(
    (e) => {
      setVerifying(true);
      e.preventDefault();
      (async () => {
        try {
          await confirmSignUp(email, verificationCode);
          if (email && password) {
            // try to login
            await authenticateUser(email, password);
            // alls well .. lets get out of here
            setTimeout(onSignUpComplete, 3000);
            //clearInterval(timer);
          }
        } catch (e) {
          setVerifying(false);
          onError(e["message"] || e);
        }
      })();
    },
    [onSignUpComplete, verificationCode, email],
  );

  const handleEmailChanged = useCallback(
    (e) => {
      setEmail(e.target.value);
      onError("");
    },
    [setEmail],
  );

  const handleFirstNameChanged = (e) => setFirstName(e.target.value);

  const handleLastNameChanged = (e) => setLastName(e.target.value);

  const handleSpecialityChanged = (v) => setSpecialty(v.target.value as string);

  const handlePasswordChanged = (e) => setPassword(e.target.value);

  const handlePasswordVerifyChanged = (e) => setVerifyPassword(e.target.value);

  const handleTermsConfirmedChange = (e) => {
    setTermsConfirmed(e.target.checked);
  };

  const isSignUpButtonDisabled =
    !email ||
    !password ||
    verifyPassword !== password ||
    (props.requireSpecialty && !specialty) ||
    (requireTermsConfirm && !termsConfirmed);

  const handleVerificationCodeChanged = useCallback(
    (e) => setVerificationCode(e.target.value),
    [setVerificationCode],
  );

  useEffect(() => {
    const timer = setInterval(async () => {
      try {
        if (verificationCodeFn) {
          if (email && password) {
            await authenticateUser(email, password);
            onSignUpComplete();
          }
        }
      } catch (e) {
        console.log(e);
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [verificationCodeFn]);

  return (
    <>
      {!verificationCodeFn && (
        <form>
          <TextField
            className={"email-input"}
            fullWidth
            required
            margin="normal"
            variant="outlined"
            label="Email Address"
            type="email"
            onChange={handleEmailChanged}
          />
          {props.requireFullName && (
            <>
              <TextField
                fullWidth
                required
                margin="normal"
                variant="outlined"
                label="First Name"
                type="string"
                onChange={handleFirstNameChanged}
              />
              <TextField
                fullWidth
                required
                margin="normal"
                variant="outlined"
                label="Last Name"
                type="string"
                onChange={handleLastNameChanged}
              />
            </>
          )}
          {props.requireSpecialty && (
            <div className={"speciality-input"}>
              <InputLabel>Speciality</InputLabel>
              <Select
                required
                fullWidth
                variant="outlined"
                value={specialty}
                onChange={handleSpecialityChanged}
                label="Speciality"
              >
                {Object.values(Specialities).map((s) => (
                  <MenuItem value={s}>{s}</MenuItem>
                ))}
              </Select>
            </div>
          )}
          <TextField
            className={"password-input"}
            fullWidth
            required
            margin="normal"
            variant="outlined"
            label="Password"
            type="password"
            onChange={handlePasswordChanged}
          />
          <TextField
            className={"password-input repeat"}
            fullWidth
            required
            margin="normal"
            variant="outlined"
            label="Confirm Password"
            type="password"
            error={verifyPassword !== password}
            onChange={handlePasswordVerifyChanged}
          />
          {requireTermsConfirm && (
            <FormControlLabel
              color={"primary"}
              control={
                <Checkbox
                  checked={termsConfirmed}
                  onChange={handleTermsConfirmedChange}
                  name="confirmTerms"
                  color={"primary"}
                />
              }
              label={
                <TermConfirmText>
                  I have read and accepted the&nbsp;
                  <Link color="primary" target="_blank" href={TERMS_OF_USE_URL}>
                    Terms of use
                  </Link>
                  ,&nbsp;
                  <Link
                    color="primary"
                    target="_blank"
                    href={PRIVACY_POLICY_URL}
                  >
                    Privacy Policy
                  </Link>
                  &nbsp;and&nbsp;
                  <Link
                    color="primary"
                    target="_blank"
                    href={COOKIE_POLICY_URL}
                  >
                    Cookie Policy
                  </Link>
                </TermConfirmText>
              }
            />
          )}
          <Button
            color="primary"
            variant="contained"
            disabled={isSignUpButtonDisabled}
            fullWidth
            onClick={onSignUp}
            className="submit-button"
          >
            Sign Up
          </Button>
          {props.children}
        </form>
      )}
      {verificationCodeFn && (
        <form>
          <KahunPrimaryTypography>
            Please click the link you received in your email. <br /> You can
            also copy the verification code from your email and enter it below.
          </KahunPrimaryTypography>
          <KahunSecondaryTypography>
            (If you don't see the email, check the spam folder for mail from
            info@kahun.com)
          </KahunSecondaryTypography>
          <KahunPrimaryTypography></KahunPrimaryTypography>
          <TextField
            fullWidth
            required
            margin="normal"
            variant="outlined"
            label="Verification Code"
            onChange={handleVerificationCodeChanged}
          />
          <Button
            className="submit-button"
            variant="contained"
            fullWidth
            color="primary"
            onClick={handleVerifyActivationCode}
            disabled={verifying}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              Verify
              {verifying && <CircularProgress size={20} />}
            </div>
          </Button>
          <span className={"navigation-link"}>
            <Button color="primary" onClick={verificationCodeFn}>
              Resend Code
            </Button>
          </span>
        </form>
      )}
    </>
  );
};
